import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { integrationsService } from '../services/integrations.service';

const COMPOSIO_STATUS_KEY = ['composio-status'];
const POPUP_POLL_INTERVAL_MS = 500;
const POPUP_TIMEOUT_MS = 5 * 60 * 1000;

export function useComposioStatus() {
  return useQuery({
    queryKey: COMPOSIO_STATUS_KEY,
    queryFn: () => integrationsService.getComposioStatus(),
  });
}

export function useComposioConnect() {
  const queryClient = useQueryClient();
  const popupRef = useRef<Window | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return useMutation({
    mutationFn: async (toolkit: string) => {
      const { connectUrl } = await integrationsService.getComposioConnectUrl(
        toolkit
      );

      const popup = window.open(
        connectUrl,
        'composio-connect',
        'width=600,height=700,left=200,top=100'
      );
      if (!popup) {
        throw new Error('Popup blocked — allow popups and try again');
      }
      popupRef.current = popup;

      return new Promise<void>((resolve, reject) => {
        const cleanup = () => {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        };

        intervalRef.current = setInterval(() => {
          if (popupRef.current?.closed) {
            cleanup();
            resolve();
          }
        }, POPUP_POLL_INTERVAL_MS);

        timeoutRef.current = setTimeout(() => {
          cleanup();
          popupRef.current?.close();
          reject(new Error('OAuth flow timed out — please try again'));
        }, POPUP_TIMEOUT_MS);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPOSIO_STATUS_KEY });
    },
  });
}
